import { scheduleAfterFirstPaint } from "../../utils/rendering.js";

// Constants and variables
const imageFormatSupport = {
  avif: false,
  webp: false,
};

const PIXEL_PRECISION = 100;
const MAX_WIDTH = window.location.hostname === "www.barcelo.com" ? 3800 : 2000;
const MAX_HEIGHT = 2000;
const IMAGE_OPTIMIZER_INIT_KEY = "__barceloImageOptimizerInitialized";

const RENDITIONS = {
  SQUARE: "square",
  LAND: "land",
  PORTRAIT: "port",
  VERTICAL: "vert",
  STRIPE: "stripe",
  DEFAULT: "optm",
};

const LAYOUT_DIMENSIONS_BY_RENDITION = {
  [RENDITIONS.SQUARE]: { width: 500, height: 500 },
  [RENDITIONS.LAND]: { width: 500, height: 250 },
  [RENDITIONS.PORTRAIT]: { width: 500, height: 1000 },
  [RENDITIONS.STRIPE]: { width: 500, height: 125 },
};

const dynamicPending = new WeakMap();

const INITIAL_VIEWPORT_OFFSET = 200;
const INITIAL_HIGH_PRIORITY_IMAGES = 4;
const INITIAL_IMAGE_SYNC_SCAN_LIMIT = 6;
let lazyImageObserverStarted = false;
let dynamicImageEventsBound = false;
let initialHighPriorityImages = 0;

const CONTAINER_WATCH_TTL_MS = 4000;

const observedContainers = new WeakMap();
const containerWatchers = new WeakMap();
const debounceTimers = new WeakMap();
let globalResizeObserver = null;

let _cachedDPR = null;
const getEffectiveDPR = () => {
  if (_cachedDPR !== null) {
    return _cachedDPR;
  }

  let dpr = window.devicePixelRatio || 1;
  const connection = navigator.connection;
  if (connection) {
    const { effectiveType, saveData, downlink } = connection;
    if (saveData || downlink < 2.0 || ["slow-2g", "2g", "3g"].includes(effectiveType)) {
      dpr = 1;
    }
  }

  _cachedDPR = dpr;
  requestAnimationFrame(() => {
    _cachedDPR = null;
  });
  return dpr;
};

const measureAfterLayout = (callback) => requestAnimationFrame(() => requestAnimationFrame(callback));

const getDynamicContainer = function (el) {
  return el.closest(".dynamicLoading");
};

const deferUntilVisible = function (img, container, run) {
  if (container.classList.contains("visibleComponent")) {
    run();
    return;
  }
  if (dynamicPending.has(img)) {
    return;
  }

  const mo = new MutationObserver(() => {
    if (container.classList.contains("visibleComponent")) {
      mo.disconnect();
      dynamicPending.delete(img);
      run();
    }
  });
  mo.observe(container, { attributes: true, attributeFilter: ["class"] });
  dynamicPending.set(img, mo);
};

const OPEN_MODAL_SELECTOR = ".brh-modal, .tingle-modal, .c-modal";

const revealIfInOpenModal = function (container) {
  const modal = container.closest(OPEN_MODAL_SELECTOR);
  if (!modal || !(modal.offsetWidth || modal.offsetHeight || modal.getClientRects().length)) {
    return false;
  }

  const loaders = container.querySelectorAll(".component-loader");
  for (let i = 0; i < loaders.length; i++) {
    if (loaders[i].closest(".dynamicLoading") === container) {
      loaders[i].remove();
    }
  }

  container.classList.remove("hiddenComponent");
  container.classList.add("visibleComponent");
  return true;
};

//------------------------  Get best image dimension for device---------------------------//

const readRawDimension = (image, isWidth = true) => {
  let container = image;
  let dimension = isWidth ? container.clientWidth : container.clientHeight;

  while (dimension === 0 && container.parentNode && container.parentNode !== document.body) {
    container = container.parentNode;
    dimension = isWidth ? container.clientWidth : container.clientHeight;
  }

  return { dimension, container };
};

const applyDimensionConstraints = (rawDimension, isWidth = true) => {
  const viewportMax = isWidth ? window.innerWidth : window.innerHeight;
  let dimension = rawDimension;

  if (dimension > viewportMax) {
    dimension = viewportMax;
  }

  const dpr = getEffectiveDPR();
  const isMobileHighRes = window.bcl?.u?.mobile?.isMobile() && dpr >= 1.75;
  const multiplier = isMobileHighRes ? dpr / 2 : dpr;

  const optimalDimension = Math.ceil((dimension * multiplier) / PIXEL_PRECISION) * PIXEL_PRECISION;
  const MAX_DIMENSION = isWidth ? MAX_WIDTH : MAX_HEIGHT;

  return optimalDimension === 0 ? PIXEL_PRECISION : Math.min(optimalDimension, MAX_DIMENSION);
};

const getOptimalDimension = (image, isWidth = true) => {
  const { dimension } = readRawDimension(image, isWidth);
  return applyDimensionConstraints(dimension, isWidth);
};

const isDimensionSuspicious = (image) => {
  const { dimension: w, container: wCont } = readRawDimension(image, true);
  const { dimension: h, container: hCont } = readRawDimension(image, false);

  if (wCont === document.body || hCont === document.body) {
    return true;
  }
  if (w === 0 || h === 0) {
    return true;
  }

  const parent = image.parentElement;
  if (parent) {
    const parentWidth = parent.clientWidth;
    if (w === window.innerWidth && parentWidth > 0 && parentWidth < w * 0.9) {
      return true;
    }
  }
  return false;
};

//--------------------- Get image rendition type -------------------------//

const getImageRendition = (image) => {
  const closestElement = image.closest("[data-rendition-breakpoints]");
  if (!closestElement) {
    return RENDITIONS.DEFAULT;
  }

  try {
    const imageType = JSON.parse(closestElement.getAttribute("data-rendition-breakpoints"));
    const isMobile = window.bcl?.u?.mobile?.isMobile();
    return isMobile ? Object.keys(imageType)[0] : Object.keys(imageType)[1] || Object.keys(imageType)[0];
  } catch (e) {
    console.warn("Invalid data-rendition-breakpoints JSON", e);
    return RENDITIONS.DEFAULT;
  }
};

//--------------------- Reserve the right layout ratio before image optimization -------------------------//

const stabilizeImageLayout = (image) => {
  if (image.dataset.layoutStabilized === "true") {
    return;
  }

  const layoutDimensions = LAYOUT_DIMENSIONS_BY_RENDITION[getImageRendition(image)];

  if (!layoutDimensions) {
    return;
  }

  image.dataset.layoutStabilized = "true";
  image.setAttribute("width", layoutDimensions.width);
  image.setAttribute("height", layoutDimensions.height);
};

//--------------------- Adapt image size to device without Dynamic media-------------------------//

const setOptimalDimensions = (image, optimalWidth) => {
  const newSrc = image.parentElement?.dataset?.cmpSrc;
  if (!newSrc) {
    return;
  }

  const imageFormat = getImageRendition(image);
  image.src = newSrc.includes("bhgimg") ? newSrc.replace("bhgimg", `bhgimg.${imageFormat}${optimalWidth}`) : newSrc;
};

const isGifImage = (image) => {
  const asset = image.parentElement?.dataset?.asset || "";
  return asset.split(/[?#]/)[0].toLowerCase().endsWith(".gif");
};

const setGifSourceDM = (image) => {
  const cmpSrc = image.parentElement?.dataset?.cmpSrc;
  if (!cmpSrc) {
    return;
  }

  const validSrc = cmpSrc.startsWith("//") ? `https:${cmpSrc}` : cmpSrc;
  const newSrc = new URL(validSrc, window.location.origin);

  if (newSrc.protocol === "http:") {
    newSrc.protocol = "https:";
  }

  newSrc.pathname = newSrc.pathname.replace("/is/image/", "/is/content/");
  newSrc.search = "";
  newSrc.hash = "";

  image.src = decodeURIComponent(newSrc.toString());
};

//--------------------- Detect if AVIF and WebP are supported ------------------------//

const canUseAvif = () =>
  new Promise((resolve) => {
    const image = new Image();
    image.addEventListener("error", () => resolve(false));
    image.addEventListener("load", () => resolve(image.width === 1));

    image.src =
      // eslint-disable-next-line @stylistic/js/max-len
      "data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUEAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAABYAAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgSAAAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAAB5tZGF0EgAKBzgADlAgIGkyCR/wAABAAACvcA==";
  }).catch(() => false);

const canUseWebp = () =>
  new Promise((resolve) => {
    const image = new Image();
    image.addEventListener("error", () => resolve(false));
    image.addEventListener("load", () => resolve(image.width === 1));
    image.src = "data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=";
  }).catch(() => false);

const getBrowserMajorVersion = (regexp) => {
  const match = regexp.exec(window.navigator.userAgent || "");
  return match ? Number.parseInt(match[1], 10) : 0;
};

const primeLikelyImageFormatSupport = () => {
  const ua = window.navigator.userAgent || "";
  const isIos = /iP(?:hone|ad|od)/.test(ua);
  const chromiumVersion = !isIos && getBrowserMajorVersion(/\b(?:Chrome|Chromium|Edg|OPR)\/(\d+)/);
  const firefoxVersion = getBrowserMajorVersion(/\bFirefox\/(\d+)/);
  const safariVersion = getBrowserMajorVersion(/\bVersion\/(\d+).+Safari\//);

  imageFormatSupport.avif = chromiumVersion >= 85 || firefoxVersion >= 93 || safariVersion >= 16;
  imageFormatSupport.webp = imageFormatSupport.avif || chromiumVersion >= 32 || firefoxVersion >= 65 || safariVersion >= 14;
};

//---------------------- Sorts Search Parameters to avoid overrides --------------//

const sortSearchParams = (url) => {
  const params = Array.from(url.searchParams.entries());
  if (params.length === 0) {
    return;
  }

  const [firstKey, firstValue] = params[0];
  if (firstKey && firstKey.startsWith("$") && firstKey.endsWith("$")) {
    url.searchParams.delete(firstKey);
    url.searchParams.set(firstKey, firstValue);
  }
};

//--------------------- Get adaptive quality based on Network -------------------------//

const getAdaptiveQuality = () => {
  if (!navigator.connection) {
    return null;
  }
  if (navigator.connection.saveData) {
    return "30";
  }

  const connectionType = navigator.connection.effectiveType;
  switch (connectionType) {
    case "slow-2g":
    case "2g":
      return "30";
    case "3g":
      return "35";
    case "4g":
      if (navigator.connection.downlink !== undefined && navigator.connection.downlink < 2.0) {
        return "40";
      }
      return null;
    default:
      return null;
  }
};

//--------------------- Adapt image size to device with Dynamic media-------------------------//

const setOptimalDimensionsDM = async function (image, optimalWidth, optimalHeight) {
  window.bcl.s.performance.processedImages = (window.bcl.s.performance.processedImages || 0) + 1;
  const position = window.bcl.s.performance.processedImages;

  const cmpSrc = image.parentElement?.dataset?.cmpSrc;

  if (!cmpSrc || !cmpSrc.includes("/is/image/")) {
    console.error("Error loading DM Image. URL invalid: " + cmpSrc);
    return;
  }

  const validSrc = cmpSrc.startsWith("//") ? `https:${cmpSrc}` : cmpSrc;
  const newSrc = new URL(validSrc, window.location.origin);

  if (newSrc.protocol === "http:") {
    newSrc.protocol = "https:";
  }

  image.classList.add(`loadPosition-${position}`);

  const skipRenditionAdjust = !!image.closest(".c-hotel-room-list__header");
  const renditionType = getImageRendition(image);
  let renditionToApply = null;

  const assetType = image.parentElement?.dataset?.asset || "";

  if (!/\/(logo|icon)/.test(assetType.toLowerCase())) {
    switch (renditionType) {
      case RENDITIONS.SQUARE:
        if (!skipRenditionAdjust) {
          optimalHeight = optimalWidth;
        }
        renditionToApply = ":Square";
        break;
      case RENDITIONS.PORTRAIT:
        if (!skipRenditionAdjust) {
          optimalHeight = optimalWidth * 2;
        }
        renditionToApply = ":Portrait";
        break;
      case RENDITIONS.VERTICAL:
        renditionToApply = ":Vertical";
        break;
      case RENDITIONS.LAND:
        if (!skipRenditionAdjust) {
          optimalHeight = optimalWidth / 2;
        }
        renditionToApply = ":Landscape";
        break;
      case RENDITIONS.STRIPE:
        if (!skipRenditionAdjust) {
          optimalHeight = optimalWidth / 4;
        }
        renditionToApply = ":Stripe";
        break;
      default:
        break;
    }

    if (optimalWidth) {
      optimalWidth = Math.min(optimalWidth, MAX_WIDTH);
    }

    if (optimalHeight) {
      optimalHeight = Math.min(optimalHeight, MAX_HEIGHT);
    }

    if (renditionToApply) {
      newSrc.pathname += renditionToApply;
    }

    if (!newSrc.searchParams.has("wid") && optimalWidth) {
      newSrc.searchParams.set("wid", optimalWidth);
    }

    if (!newSrc.searchParams.has("hei") && optimalHeight) {
      newSrc.searchParams.set("hei", optimalHeight);
    }

    if ((newSrc.searchParams.has("wid") || newSrc.searchParams.has("hei")) && !newSrc.searchParams.has("fit")) {
      newSrc.searchParams.set("fit", "crop,1");
    }

    if (!newSrc.searchParams.has("qlt")) {
      const adaptiveQuality = getAdaptiveQuality();
      const defaultQuality = window.bcl?.u?.mobile?.isMobile() ? "50" : imageFormatSupport.avif ? "60" : "75";
      newSrc.searchParams.set("qlt", adaptiveQuality || defaultQuality);
    }
  } else {
    if (!newSrc.searchParams.has("qlt")) {
      const adaptiveQuality = getAdaptiveQuality();
      newSrc.searchParams.set("qlt", adaptiveQuality || "50");
    }
  }

  const format = imageFormatSupport.avif ? "avif-alpha" : imageFormatSupport.webp ? "webp-alpha" : newSrc.toString().includes(".png") ? "png8-alpha" : "pjpeg";
  newSrc.searchParams.set("fmt", format);

  newSrc.searchParams.set("cache", "update,on,on");
  newSrc.searchParams.set("defaultImage", "default-dm");
  if (!newSrc.searchParams.has("dpr")) {
    newSrc.searchParams.set("dpr", "off");
  }

  const device = window.bcl?.u?.mobile?.isMobile() ? "mobile" : "desktop";
  const alignment = image.closest(`[data-${device}-alignment]`)?.dataset[`${device}Alignment`];

  if (alignment) {
    const [horizontal, vertical] = alignment.split("-").map((val) => (val === "left" ? -1 : val === "right" ? 1 : 0));
    newSrc.searchParams.set("align", [horizontal, vertical].join(","));
  }

  if (image.closest(".bwd-commitment")) {
    newSrc.searchParams.set("dpr", "on,2");
  }

  sortSearchParams(newSrc);

  const finalUrl = decodeURIComponent(newSrc.toString());
  document.querySelectorAll(`img.loadPosition-${position}`).forEach((img) => {
    img.src = finalUrl;
  });
};

const unwatchContainer = (image) => {
  const watchData = containerWatchers.get(image);
  if (!watchData) {
    return;
  }

  const { container, timeoutId } = watchData;
  clearTimeout(timeoutId);
  containerWatchers.delete(image);

  const images = observedContainers.get(container);
  if (images) {
    images.delete(image);
    if (images.size === 0) {
      observedContainers.delete(container);
      if (globalResizeObserver) {
        globalResizeObserver.unobserve(container);
      }
    }
  }
};

const watchForContainerResize = (image) => {
  if (!("ResizeObserver" in window)) {
    return;
  }

  unwatchContainer(image);

  const container = image.parentElement || image;
  if (!container || container === document.body) {
    return;
  }

  if (!globalResizeObserver) {
    globalResizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const targetContainer = entry.target;
        const images = observedContainers.get(targetContainer);
        if (!images) {
          continue;
        }

        images.forEach((img) => {
          clearTimeout(debounceTimers.get(img));
          const timerId = setTimeout(() => {
            if (!img.isConnected) {
              unwatchContainer(img);
              return;
            }
            if (targetContainer.clientWidth === 0 || targetContainer.clientHeight === 0) {
              return;
            }
            if (img.dataset.optimized !== "true") {
              optimizeImage(img);
            }
            if (img.dataset.optimized === "true") {
              unwatchContainer(img);
            }
          }, 80);
          debounceTimers.set(img, timerId);
        });
      }
    });
  }

  let images = observedContainers.get(container);
  if (!images) {
    images = new Set();
    observedContainers.set(container, images);
    globalResizeObserver.observe(container);
  }
  images.add(image);

  const timeoutId = setTimeout(() => {
    if (image.isConnected && image.dataset.optimized !== "true") {
      optimizeImage(image, null, null, true);
    }
    unwatchContainer(image);
  }, CONTAINER_WATCH_TTL_MS);

  containerWatchers.set(image, { container, timeoutId });
};

const optimizeImage = (image, precomputedWidth = null, precomputedHeight = null, force = false) => {
  if (image.dataset.optimized === "true") {
    return;
  }

  if (!force && isDimensionSuspicious(image)) {
    watchForContainerResize(image);
    return;
  }

  stabilizeImageLayout(image);

  const isDM = image.parentElement?.dataset?.isDm === "true";

  if (isDM && isGifImage(image)) {
    setGifSourceDM(image);
    image.dataset.optimized = "true";
    return;
  }

  image.dataset.optimized = "true";

  const optimalWidth = precomputedWidth ?? getOptimalDimension(image, true);
  const optimalHeight = precomputedHeight ?? getOptimalDimension(image, false);

  if (isDM) {
    setOptimalDimensionsDM(image, optimalWidth, optimalHeight);
  } else {
    setOptimalDimensions(image, optimalWidth);
  }
};

const prioritizeInitialImage = (image, highPriority = false) => {
  image.loading = "eager";

  if (highPriority && image.closest("main") && initialHighPriorityImages < INITIAL_HIGH_PRIORITY_IMAGES) {
    image.fetchPriority = "high";
    image.setAttribute("fetchpriority", "high");
    initialHighPriorityImages += 1;
  }
};

const getViewportSize = () => ({
  height: window.innerHeight || document.documentElement.clientHeight || 0,
  width: window.innerWidth || document.documentElement.clientWidth || 0,
});

const isRenderableInViewport = (image, viewport = getViewportSize()) => {
  const rect = image.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 && rect.bottom >= 0 && rect.right >= 0 && rect.top <= viewport.height && rect.left <= viewport.width;
};

const optimizeVisibleImages = (root = document) => {
  window.requestAnimationFrame(() => {
    const candidates = root.querySelectorAll?.("img[data-barcelo-image='true']:not([data-optimized='true'])");
    if (!candidates || !candidates.length) {
      return;
    }

    const viewport = getViewportSize();

    const pending = [];
    candidates.forEach((img) => {
      if (!img.isConnected || !isRenderableInViewport(img, viewport)) {
        return;
      }
      if (isDimensionSuspicious(img)) {
        pending.push({ img, suspicious: true });
        return;
      }
      pending.push({
        img,
        suspicious: false,
        width: getOptimalDimension(img, true),
        height: getOptimalDimension(img, false),
      });
    });

    pending.forEach(({ img, suspicious, width, height }) => {
      prioritizeInitialImage(img);
      if (suspicious) {
        watchForContainerResize(img);
        return;
      }
      optimizeImage(img, width, height, true);
    });
  });
};

const isNearInitialViewport = (image, viewport = getViewportSize()) => {
  if (!image?.getBoundingClientRect) {
    return false;
  }

  const rect = image.getBoundingClientRect();

  return rect.bottom >= -INITIAL_VIEWPORT_OFFSET && rect.top <= viewport.height + INITIAL_VIEWPORT_OFFSET && rect.right >= 0 && rect.left <= viewport.width;
};

const optimizeInitialImages = () => {
  const images = Array.from(document.querySelectorAll("img[data-barcelo-image='true']:not([data-optimized='true'])"));
  const hasCriticalVideoPoster = !!document.querySelector(".heroslider .video-poster-overlay");

  const syncImages = hasCriticalVideoPoster ? [] : images.slice(0, INITIAL_IMAGE_SYNC_SCAN_LIMIT);
  const deferredImages = hasCriticalVideoPoster ? images : images.slice(INITIAL_IMAGE_SYNC_SCAN_LIMIT);

  syncImages.forEach(stabilizeImageLayout);

  measureAfterLayout(() => {
    const viewport = getViewportSize();
    syncImages.forEach((img) => {
      if (img.dataset.optimized === "true") {
        return;
      }

      if (isNearInitialViewport(img, viewport)) {
        const dynContainer = getDynamicContainer(img);
        if (dynContainer && !dynContainer.classList.contains("visibleComponent")) {
          deferUntilVisible(img, dynContainer, () => {
            prioritizeInitialImage(img, true);
            optimizeImage(img);
          });
          return;
        }

        prioritizeInitialImage(img, true);
        optimizeImage(img);
      } else {
        img.loading = "lazy";
      }
    });
  });

  if (deferredImages.length === 0) {
    return;
  }

  scheduleAfterFirstPaint(() => {
    deferredImages.forEach((img) => {
      if (img.dataset.optimized !== "true") {
        stabilizeImageLayout(img);
      }
    });

    measureAfterLayout(() => {
      const viewport = getViewportSize();
      deferredImages
        .filter((img) => img.dataset.optimized !== "true")
        .forEach((img) => {
          if (isNearInitialViewport(img, viewport)) {
            const dynContainer = getDynamicContainer(img);
            if (dynContainer && !dynContainer.classList.contains("visibleComponent")) {
              deferUntilVisible(img, dynContainer, () => {
                prioritizeInitialImage(img, true);
                optimizeImage(img);
              });
              return;
            }
            prioritizeInitialImage(img, true);
            optimizeImage(img);
          } else {
            img.loading = "lazy";
          }
        });
    });
  });
};

const startLazyImageObserver = () => {
  if (lazyImageObserverStarted) {
    return;
  }

  lazyImageObserverStarted = true;

  const images = document.querySelectorAll("img[data-barcelo-image='true']:not([data-optimized='true'])");

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        const img = entry.target;
        if (!entry.isIntersecting) {
          return;
        }

        if (!img.isConnected || img.dataset.optimized === "true") {
          return;
        }

        const dynContainer = getDynamicContainer(img);
        if (dynContainer && !dynContainer.classList.contains("visibleComponent")) {
          revealIfInOpenModal(dynContainer);
        }
        prioritizeInitialImage(img);
        optimizeImage(img);
        obs.unobserve(img);
      });
    },
    {
      rootMargin: "10%",
    },
  );

  images.forEach((img) => {
    stabilizeImageLayout(img);
    img.loading = "lazy";
    observer.observe(img);
  });

  const mutationObserver = new MutationObserver((mutations) => {
    const newImages = [];
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.querySelectorAll) {
          node.querySelectorAll("img[data-barcelo-image='true']:not([data-optimized='true'])").forEach((img) => newImages.push(img));
        }
      });
    });

    if (newImages.length === 0) {
      return;
    }

    const processNewImages = () => {
      const viewport = getViewportSize();
      newImages.forEach((img) => {
        stabilizeImageLayout(img);

        if (isNearInitialViewport(img, viewport)) {
          const dynContainer = getDynamicContainer(img);
          if (dynContainer && !dynContainer.classList.contains("visibleComponent")) {
            if (revealIfInOpenModal(dynContainer)) {
              prioritizeInitialImage(img, true);
              optimizeImage(img);
              return;
            }
            deferUntilVisible(img, dynContainer, () => {
              prioritizeInitialImage(img, true);
              optimizeImage(img);
            });
            return;
          }

          prioritizeInitialImage(img, true);
          optimizeImage(img);
        } else {
          img.loading = "lazy";
          observer.observe(img);
        }
      });
    };

    if ("requestIdleCallback" in window) {
      requestIdleCallback(processNewImages, { timeout: 500 });
    } else {
      setTimeout(processNewImages, 50);
    }
  });

  mutationObserver.observe(document, { childList: true, subtree: true });
};

let stylesLoadedScanQueued = false;

const bindDynamicImageEvents = () => {
  if (dynamicImageEventsBound) {
    return;
  }

  dynamicImageEventsBound = true;

  document.addEventListener("stylesLoaded", () => {
    if (stylesLoadedScanQueued) {
      return;
    }
    stylesLoadedScanQueued = true;
    window.requestAnimationFrame(() => {
      stylesLoadedScanQueued = false;
      optimizeVisibleImages(document);
    });
  });

  document.addEventListener("modal-open", (event) => {
    optimizeVisibleImages(event.detail || document);
  });

  document.addEventListener("brh:modal:open", () => {
    optimizeVisibleImages(document.querySelector(".brh-modal") || document);
  });
};

//----------------------------- Pre-requisites checks --------------------//

const checkPrerequisites = async () => {
  const [avifSupported, webpSupported] = await Promise.all([canUseAvif(), canUseWebp()]);
  imageFormatSupport.avif = avifSupported;
  imageFormatSupport.webp = webpSupported;
};

const scheduleLazyImageObserver = () => {
  const startObserver = () => scheduleAfterFirstPaint(startLazyImageObserver);
  const startOnInteraction = () => {
    document.removeEventListener("scroll", startOnInteraction, true);
    document.removeEventListener("touchstart", startOnInteraction, true);
    document.removeEventListener("pointerdown", startOnInteraction, true);
    document.removeEventListener("keydown", startOnInteraction, true);
    startObserver();
  };

  document.addEventListener("scroll", startOnInteraction, { once: true, passive: true, capture: true });
  document.addEventListener("touchstart", startOnInteraction, { once: true, passive: true, capture: true });
  document.addEventListener("pointerdown", startOnInteraction, { once: true, passive: true, capture: true });
  document.addEventListener("keydown", startOnInteraction, { once: true, capture: true });

  window.addEventListener(
    "load",
    () => {
      if ("requestIdleCallback" in window) {
        requestIdleCallback(() => startOnInteraction(), { timeout: 2000 });
      } else {
        setTimeout(startOnInteraction, 200);
      }
    },
    { once: true },
  );
};

//----------------------------- General image functionality --------------------//

const initImages = async () => {
  window.bcl = window.bcl || {};
  window.bcl.s = window.bcl.s || {};
  window.bcl.s.performance = window.bcl.s.performance || {};

  primeLikelyImageFormatSupport();

  optimizeInitialImages();

  const startEvents = () => {
    bindDynamicImageEvents();
    scheduleLazyImageObserver();
  };

  if (document.readyState !== "loading") {
    startEvents();
  } else {
    document.addEventListener("DOMContentLoaded", startEvents, { once: true });
  }

  checkPrerequisites();
};

//------------------ Launch image functionality --------------------//

if (!window[IMAGE_OPTIMIZER_INIT_KEY]) {
  window[IMAGE_OPTIMIZER_INIT_KEY] = true;
  initImages();
}
